import React, { Component } from "react";
import { Provider } from "react-redux";
import { Routes, Route, useLocation } from "react-router-dom";
import ReactModal from "react-modal";
import axios from "axios";
import apiUrlConfig from "../environmentSettings";
import createAppStore from "../store/configureStore";
import WithLoader from "../components/HOC/WithLoader";

import AppWrapper from "./AppWrapper";
import TopNav from "../components/Display/TopNav/TopNav";
import OrganizationListing from "./Organization/OrganizationListing.js";
// Route-level code splitting: Organization hosts the dashboard builder, query
// builder and ALL charting libraries (FusionCharts, ag-grid, Recharts…) — by
// far the heaviest part of the bundle. Lazy-loading it keeps the initial
// bundle (login / org listing) small and lets the browser paint immediately
// while the dashboard chunk downloads in parallel.
const Organization = React.lazy(() => import("./Organization/Organization.js"));

import Notification from "../components/Miscellaneous/Notification/NotificationInitializer";
import ProtectedRoute from "../components/Miscellaneous/ProtectedRoute";
import CacheBuster from "../components/Miscellaneous/CacheBuster";

import personProfileIcon from "../assets/person-profile.jpg";
import { initialiseAmplitude } from "../services/DashboardDesigner/amplitudeService";
import { trackPageThroughGA } from "../services/googleAnalytics";
import { HelpWidget } from "../components/Miscellaneous/HelpWidget";
import ContextualAIAssistant from "../ContextualAIAssistant";
import { getOrganizationUser } from "../apiFactory/Organization";

// Super-admin email that gets the AI Assistant on every org. To enable for
// additional users, add their email (lowercase) to the allowlist below.
// Comparison is case-insensitive and trims whitespace.
const AI_ASSISTANT_SUPER_ADMIN_EMAILS = ["ag@ng.com"];

function ContextualAIAssistantGate(props) {
	// Resolve the current user's email via the existing organizationUsers/findOne
	// endpoint. The path needs an orgId — we grab it from the URL (first segment),
	// which is always present once the user is inside an org route. On the org
	// listing screen there's no orgId, so we don't render the assistant there.
	const location = useLocation();
	const orgId = location.pathname.split("/")[1];
	const [email, setEmail] = React.useState(null);
	const [resolved, setResolved] = React.useState(false);

	React.useEffect(() => {
		let cancelled = false;
		setResolved(false);
		setEmail(null);
		const userId = localStorage.getItem("$LoopBack$currentUserId");
		if (!userId || !orgId || !/^\d+$/.test(orgId)) {
			setResolved(true);
			return undefined;
		}
		getOrganizationUser(orgId, userId)
			.then((response) => {
				if (cancelled) return;
				const found = response?.user?.email ?? null;
				if (found) setEmail(String(found).trim().toLowerCase());
				setResolved(true);
			})
			.catch(() => {
				if (!cancelled) setResolved(true);
			});
		return () => {
			cancelled = true;
		};
	}, [orgId]);

	if (!resolved) return null;
	if (!email) return null;
	if (!AI_ASSISTANT_SUPER_ADMIN_EMAILS.includes(email)) return null;
	return <ContextualAIAssistant {...props} />;
}

const initialState = {
	widgets: [],
	previewWidgets: [],
	dashboardContainer: [],
	settings: {},
	userInfo: {
		orgRole: "",
		adminRoles: [],
		userId: ""
	},
	widgetHash: {
		widgetHash: {},
		widgetComponent: {}
	},
	organization: {},
	dashboards: [],
	currentDashboardId: null,
	currentWidgetId: null,
	userAppsInfo: {},
	organizationID: null,
	mode: "query",
	baseTable: "",
	activeStageIdx: -1,
	lastExecutedStage: -1,
	qid: "",
	keywords: {
		list: [],
		isFetching: false
	},
	queryModel: {
		stagesModel: []
	}
};

export const store = createAppStore(initialState);

ReactModal.setAppElement("#root");

initialiseAmplitude(localStorage.getItem("$LoopBack$currentUserId"), window.location.href);
trackPageThroughGA(window.location.pathname, {});

class App extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true
		};
		
		// These values come from auth frame service
		this.accessToken = localStorage.getItem("$LoopBack$accessTokenId");
		this.userId = localStorage.getItem("$LoopBack$currentUserId");
		
		// Setting default base url for axios with fallback
		const baseURL = apiUrlConfig?.apiConfig?.baseUrl || '/api';
		axios.defaults.baseURL = baseURL;
		
		// Request interceptor
		axios.interceptors.request.use(
			(config) => {
				if (this.accessToken) {
					config.headers.Authorization = this.accessToken;
				}
				return config;
			},
			(error) => {
				console.error('Request error:', error);
				return Promise.reject(error);
			}
		);

		// Response interceptor
		axios.interceptors.response.use(
			(response) => {
				// Only reject non-JSON for error responses; 2xx non-JSON (e.g. 202 from download endpoint) pass through
				const contentType = response.headers['content-type'];
				if (contentType && !contentType.includes('application/json') && response.status >= 400) {
					throw new Error('Invalid response type');
				}
				return response;
			},
			(error) => {
				if (error.response) {
					switch (error.response.status) {
						case 403:
							// Handle forbidden
							console.error('Forbidden access');
							break;
						case 404:
							// Handle not found
							console.error('Resource not found');
							break;
						case 500:
							// Handle server error
							console.error('Server error');
							break;
						default:
							console.error('API error:', error.response.status);
							break;
					}
				} else if (error.request) {
					// Request was made but no response received
					console.error('No response received:', error.request);
				} else {
					// Something else happened
					console.error('Error:', error.message);
				}
				return Promise.reject(error);
			}
		);
	}

	componentDidMount() {
		let that = this;
		setTimeout(function() {
			that.setState({ loading: false });
		}, 1000);
	}

	render() {
		return (
			<CacheBuster>
				{({ loading, isLatestVersion }) => {
					if (loading) return null;
					if (!loading && !isLatestVersion) return null;

					console.log("-----------------> redering App.js <-----------------")

					const enableAI = apiUrlConfig?.ENABLE_AI_ASSISTANT;
					// The proxy lives at thinker.uniqus.com under /api/ai. Build
					// an absolute URL because the webpack dev-server proxy at
					// webpack.config.js:250 forwards /api/* to p3fyUrl (api host),
					// NOT to thinker — so a relative "/api/ai" hits the wrong
					// host in dev. Absolute URL works in both dev and prod
					// because thinker already serves CORS for the canvas origin
					// (every other axios call uses baseURL = thinker too).
					const aiProxyEndpoint =
						apiUrlConfig?.aiProxyEndpoint ??
						(apiUrlConfig?.apiConfig?.baseUrl
							? `${apiUrlConfig.apiConfig.baseUrl.replace(/\/+$/, "")}/api/ai`
							: "/api/ai");

					return (
						<WithLoader loading={this.state.loading}>
							<Provider store={store}>
								<AppWrapper>
									<Notification />
									<React.Suspense fallback={<WithLoader loading />}>
										<Routes>
											<Route path="/:organizationId/*" element={<Organization />} />
											<Route path="/" element={
												<>
													<TopNav />
													<OrganizationListing />
												</>
											} />
										</Routes>
									</React.Suspense>
									{enableAI && this.accessToken && (
										<ContextualAIAssistantGate
											apiEndpoint={aiProxyEndpoint}
											searchProvider="brave"
											mode="floating"
											position="bottom-right"
											title="Canvas AI Assistant"
											contextSelector="body"
										/>
									)}
								</AppWrapper>
							</Provider>
						</WithLoader>
					);
				}}
			</CacheBuster>
		);
	}
}

export default App;
